import { HttpErrorResponse } from "@angular/common/http";
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  Validators,
} from "@angular/forms";
import { NgbDateParserFormatter } from "@ng-bootstrap/ng-bootstrap";
import { AlertController, Events, ModalController } from "ionic-angular";
import { Subscription } from "rxjs";
import { AuthService } from "../../../app/auth/auth.service";
import { ClearinghouseConfig, Edi } from "../../../models/payers/payer";
import { Config } from "../../../pages/patient-chart/patient-benefits/models";
import { PayersService } from "../../../providers/payers-service/payers.service";
import { StateManagementService } from "../../../providers/state-management/state-management.service";
import { UtilityService } from "../../../providers/utility-service/utility-service";
import { NgbCustomParserFormatter } from "../../common/date-picker/ngb-custom-parser-formatter";
import { SharedEligibilityService } from "../eligibility/shared-eligibility";

@Component({
  selector: "payer-edi",
  templateUrl: "payer-edi.html",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PayerEdi),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => PayerEdi),
      multi: true,
    },
    { provide: NgbDateParserFormatter, useClass: NgbCustomParserFormatter },
  ],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class PayerEdi implements OnInit, OnDestroy {
  @Input() currentPayer: any;
  @Input() showScheduledEligibility: any;
  @Output() onPayerEDISaved = new EventEmitter<any>();
  @Output() onCanceled = new EventEmitter<any>();

  ediFormGroup: FormGroup;
  subscriptions: Subscription[] = [];
  saveEnabled: boolean = false;
  selectedBenefitType: string = "";
  callEdi = false;
  editMode: boolean = false;
  benefitTypes = new Map<string, any>([
    [
      "HOSPICE",
      {
        displayName: "Hospice",
      },
    ],
    [
      "PALLIATIVE",
      {
        displayName: "Palliative",
      },
    ],
    [
      "ROOMANDBOARD",
      {
        displayName: "Room and Board",
      },
    ],
  ]);

  payerTypeCodeList: Config[] = [
    { name: "(09) Self Pay", value: "09", disabled: false },
    { name: "(11) Other Non-Federal Programs", value: "11", disabled: false },
    {
      name: "(12) Preferred Provider Organization (PPO)",
      value: "12",
      disabled: false,
    },
    {
      name: "(14) Exclusive Provider Organization (EPO)",
      value: "14",
      disabled: false,
    },
    { name: "(15) Indemnity Insurance", value: "15", disabled: false },
    {
      name: "(16) Health Maintenance Organization (HMO) Medicare Risk",
      value: "16",
      disabled: false,
    },
    { name: "(BL) Blue Cross/Blue Shield", value: "BL", disabled: false },
    { name: "(CH) CHAMPUS", value: "CH", disabled: false },
    { name: "(CI) Commercial Insurance Co.", value: "CI", disabled: false },
    {
      name: "(HM) Health Maintenance Organization",
      value: "HM",
      disabled: false,
    },
    { name: "(MA) Medicare Part A", value: "MA", disabled: false },
    { name: "(MB) Medicare Part B", value: "MB", disabled: false },
    { name: "(MC) Medicaid", value: "MC", disabled: false },
    { name: "(OF) Other Federal Programs", value: "OF", disabled: false },
    { name: "(VA) Veterans Affairs Plan", value: "VA", disabled: false },
    {
      name: "(WC) Workers Compensation Health Claim",
      value: "WC",
      disabled: false,
    },
  ];

  clearinghouseProfileList: ClearinghouseConfig[];

  submitterIdRequired: boolean = false;

  // CO-3393 (epic CO-2315): flag-gated Provider Secondary ID (REF*G2)
  // controls. Rendered only for new-model Medicaid payers; MCP plans
  // require it by default, FFS does not (A.2.1/A.2.3). Server-side
  // validation in payer-functions-save-payer-edi is authoritative.
  californiaMediCalSupportToggle: boolean = false;

  constructor(
    private fb: FormBuilder,
    private payersService: PayersService,
    private utilityService: UtilityService,
    protected alertController: AlertController,
    public modalCtrl: ModalController,
    private authSvc: AuthService,
    public sharedEligibility: SharedEligibilityService,
    public events: Events,
    private stateManagementService: StateManagementService
  ) {}

  get showSecondaryProviderId() {
    return (
      this.californiaMediCalSupportToggle &&
      this.currentPayer &&
      this.currentPayer.payerType &&
      this.currentPayer.payerType.toUpperCase() === "MEDICAID" &&
      (this.currentPayer.state ||
        this.currentPayer.isManagedCare ||
        this.currentPayer.parentStateMedicaidPayerId)
    );
  }

  get secondaryProviderIdRequired() {
    if (!this.showSecondaryProviderId) {
      return false;
    }
    // CO-3701: an MCP (managed-care) payer's Secondary Provider ID is Curantis-Managed and
    // UNCONDITIONALLY required (REQ-007). There is no admin-facing toggle for it, so the
    // required state must not depend on the (now-hidden) checkbox control.
    if (this.currentPayer.isManagedCare) {
      return true;
    }
    return !!this.ediFormGroup.get("ediSecondaryProviderIdRequired").value;
  }

  // CO-3701: the admin-facing "Required for this payer" toggle exists only for
  // fee-for-service payers. An MCP payer is Curantis-Managed (always required per REQ-007),
  // so the toggle is not shown — the field renders as a plain required field.
  get showSecondaryProviderIdToggle() {
    return this.showSecondaryProviderId && !this.currentPayer.isManagedCare;
  }

  // CO-2315 F6.S5: the state Medicaid programmes whose FEE-FOR-SERVICE line
  // requires the payer-level secondary provider ID. California is deliberately
  // absent — Medi-Cal FFS bills on NPI alone (AC-007-2). Mirrors
  // feeForServiceRequiresSecondaryProviderId in payer-functions'
  // medicaid_classification_validation.go; the server is authoritative and this
  // exists so the form does not present a field as optional that the save will
  // then reject.
  static readonly FFS_REQUIRES_SECONDARY_PROVIDER_ID = ["TX"];

  // The default required state when the payer carries no explicit toggle:
  // managed care requires a plan-specific identifier in every state, and
  // fee-for-service requires one only where the programme does.
  get secondaryProviderIdRequiredDefault(): boolean {
    if (!this.currentPayer) {
      return false;
    }
    if (this.currentPayer.isManagedCare) {
      return true;
    }
    const state = (this.currentPayer.state || "").trim().toUpperCase();
    return PayerEdi.FFS_REQUIRES_SECONDARY_PROVIDER_ID.indexOf(state) !== -1;
  }

  // F6.S5: Texas calls the identifier a TPI, so the field says so. Every other
  // programme keeps the generic REF*G2 label.
  get secondaryProviderIdLabel(): string {
    const state = (this.currentPayer && (this.currentPayer.state || ""))
      .trim()
      .toUpperCase();
    return state === "TX"
      ? "Texas Provider Identifier (TPI) — REF*G2"
      : "Provider Secondary ID (REF*G2)";
  }

  updateSecondaryProviderIdValidators() {
    const control = this.ediFormGroup.get("ediSecondaryProviderId");
    if (this.secondaryProviderIdRequired) {
      control.setValidators([Validators.required, Validators.maxLength(30)]);
    } else {
      control.setValidators([Validators.maxLength(30)]);
    }
    control.updateValueAndValidity();
  }

  getClearingHouseProfie() {
    this.payersService.getClearinghouseOptions().subscribe(
      (data) => {
        if (data) {
          this.clearinghouseProfileList = [];
          this.clearinghouseProfileList.push({
            name: "None",
            value: "None",
            disabled: false,
            clearinghouseData: null,
          });
          data.forEach((ch) => {
            this.addData(ch);
          });
        }
      },
      (err) => {
        console.log("unable to load clearinghouse");
      }
    );
  }

  addData(data) {
    let config = {} as ClearinghouseConfig;
    config.name = data.clearinghouseProfileName;
    config.value = data.clearinghouseId.toString();
    config.clearinghouseData = data;
    config.disabled = false;
    this.clearinghouseProfileList.push(config);
  }

  ngOnDestroy(): void {}

  ngOnInit(): void {
    if (this.callEdi == false) {
      this.initForm();
      this.loadEdiDataFromPayer();
    }
    this.getClearingHouseProfie();
    this.subscriptions.push(
      this.stateManagementService.californiaMediCalSupportToggle.subscribe(
        (enabled) => {
          this.californiaMediCalSupportToggle = !!enabled;
          if (this.ediFormGroup) {
            this.updateSecondaryProviderIdValidators();
          }
        }
      )
    );
  }

  ngOnChanges() {
    this.callEdi = true;
    this.initForm();
    this.loadEdiDataFromPayer();
    this.getClearingHouseProfie();
  }

  initForm() {
    this.ediFormGroup = this.fb.group({
      ediPayerName: ["", [Validators.required]],
      ediReceiverId: [
        "",
        [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(15),
        ],
      ],
      ediSubmitterId: ["", [Validators.minLength(2), Validators.maxLength(15)]],
      clearinghouseProfile: [""],
      ediPayerTypeCode: ["", [Validators.required]],
      ediSecondaryProviderId: ["", [Validators.maxLength(30)]],
      ediSecondaryProviderIdRequired: [false],
    });

    this.subscriptions.push(
      this.ediFormGroup.valueChanges.subscribe(this.onFormChanges),
      this.ediFormGroup
        .get("ediSecondaryProviderIdRequired")
        .valueChanges.subscribe(() =>
          this.updateSecondaryProviderIdValidators()
        )
    );
  }

  onFormChanges = (value) => {
    if (value) {
      this.saveEnabled = this.ediFormGroup.valid;
    }
  };

  loadEdiDataFromPayer() {
    if (this.currentPayer && this.currentPayer.benefitType) {
      let first: boolean = true;
      this.currentPayer.benefitType.split(",").forEach((t) => {
        t = t.replace(/ /g, "");
        t = t.toUpperCase();
        if (this.benefitTypes.has(t)) {
          const displayName = this.benefitTypes.get(t).displayName;
          if (this.currentPayer.claimRules) {
            this.editMode = true;
            this.benefitTypes.set(t, {
              payerName: this.currentPayer.claimRules.ediPayerName,
              receiverId: this.currentPayer.claimRules.receiverId,
              submitterId: this.currentPayer.claimRules.submitterId,
              payerTypeCode: this.currentPayer.payerSubtype,
              displayName: displayName,
            });

            if (Array.isArray(this.currentPayer.claimRules.edi)) {
              this.currentPayer.claimRules.edi.forEach((edi) => {
                if (edi.benefitType == t) {
                  this.benefitTypes.set(t, {
                    payerName: edi.payerName,
                    receiverId: edi.receiverId,
                    submitterId: edi.submitterId,
                    payerTypeCode: edi.payerTypeCode,
                    displayName: displayName,
                    clearinghouseProfile:
                      edi.clearinghouseId == "" ? "None" : edi.clearinghouseId,
                    secondaryProviderId: edi.secondaryProviderId || "",
                    secondaryProviderIdRequired:
                      edi.secondaryProviderIdRequired != null
                        ? edi.secondaryProviderIdRequired
                        : this.secondaryProviderIdRequiredDefault,
                  });
                }
              });
              this.onPayerEDISaved.emit();
            }
          } else {
            this.benefitTypes.set(t, {
              payerName: "",
              receiverId: "",
              submitterId: "",
              payerTypeCode: "",
              displayName: "",
              clearinghouseProfile: "None",
            });
          }

          if (first) {
            first = false;
            this.changeBenefitType(t);
          }
        }
      });
    }
  }

  changeBenefitType(benefitType: string) {
    this.selectedBenefitType = benefitType;
    this.ediFormGroup
      .get("ediPayerName")
      .setValue(this.benefitTypes.get(benefitType).payerName);
    this.ediFormGroup
      .get("ediReceiverId")
      .setValue(this.benefitTypes.get(benefitType).receiverId);
    this.ediFormGroup
      .get("ediSubmitterId")
      .setValue(this.benefitTypes.get(benefitType).submitterId);
    this.ediFormGroup
      .get("ediPayerTypeCode")
      .setValue(this.benefitTypes.get(benefitType).payerTypeCode);
    this.ediFormGroup
      .get("clearinghouseProfile")
      .setValue(this.benefitTypes.get(benefitType).clearinghouseProfile);
    this.ediFormGroup
      .get("ediSecondaryProviderId")
      .setValue(this.benefitTypes.get(benefitType).secondaryProviderId || "");
    this.ediFormGroup
      .get("ediSecondaryProviderIdRequired")
      .setValue(
        this.benefitTypes.get(benefitType).secondaryProviderIdRequired != null
          ? this.benefitTypes.get(benefitType).secondaryProviderIdRequired
          : this.secondaryProviderIdRequiredDefault
      );
    this.updateSecondaryProviderIdValidators();

    this.updateSubmitterIdValidators(
      this.ediFormGroup.get("clearinghouseProfile").value
    );

    this.saveEnabled = false;
  }

  showSavePayerModal() {
    if (this.editMode) {
      let modalPage = this.modalCtrl.create(
        "SaveFacilityPage",
        {
          title: "Payer Information Change",
          commentPlaceholderKey: "archiveFacilityCommentPlaceHolder",
          popupMessageKey: "savePayerWarningMessage",
          section: "payer",
        },
        {
          enableBackdropDismiss: false,
          cssClass: "save-payer-confirmation-popup",
        }
      );
      modalPage.onDidDismiss((data) => {
        if (data.save) {
          this.saveEdi();
        }
      });
      modalPage.present();
    } else {
      this.saveEdi();
    }
  }

  saveEdi(): void {
    let request = this.createRequest();
    let displayName = this.benefitTypes.get(request.benefitType).displayName;
    this.utilityService.increaseSpinnerCount("Loading");
    this.payersService
      .postPayerEdi(this.currentPayer.payerId, request)
      .subscribe(() => {
        setTimeout(() => {
          if (
            this.currentPayer.claimRules &&
            this.currentPayer.claimRules.edi
          ) {
            let detailValue = this.currentPayer.claimRules.edi.filter(
              (x) => x.benefitType == request.benefitType
            )[0];
            if (detailValue) {
              detailValue.clearinghouseId = request.clearinghouseId;
              this.currentPayer.claimRules.edi.filter(
                (x) => x.benefitType == request.benefitType
              )[0] = detailValue;
            } else {
              this.currentPayer.claimRules.edi.push(request);
            }
          } else {
            this.currentPayer.claimRules.edi = [];
            this.currentPayer.claimRules.edi.push(request);
          }

          if (this.currentPayer.instantEligibilityClearinghouseId) {
            this.currentPayer.instantEligibilityClearinghouseId =
              this.currentPayer.instantEligibilityClearinghouseId;
          }

          if (this.showScheduledEligibility) {
            this.currentPayer.eligibilityUpdated = true;
            this.events.publish("currentData", this.currentPayer);
          }
          //this.sharedEligibility.setPayer(this.currentPayer);
          this.updateEdiData(request, displayName);
          this.saveEnabled = false;
          this.utilityService.decreaseSpinnerCount();
          this.onPayerEDISaved.emit();
          this.showAlert({
            title: "Success",
            message: displayName + " EDI information saved.",
          });
        }, 1000);
      }, this.onSaveError);
  }

  cancel(): void {
    this.onCanceled.emit(true);
  }

  createRequest(): Edi {
    const edi: Edi = {} as Edi;
    edi.benefitType = this.selectedBenefitType;
    edi.payerName = this.ediFormGroup.get("ediPayerName").value;
    edi.receiverId = this.ediFormGroup.get("ediReceiverId").value;
    edi.submitterId = this.ediFormGroup.get("ediSubmitterId").value;
    edi.clearinghouseTenantName = this.authSvc.getUser().tenantName;
    edi.clearinghouseId =
      this.ediFormGroup.get("clearinghouseProfile").value == "None"
        ? ""
        : this.ediFormGroup.get("clearinghouseProfile").value == null
        ? ""
        : this.ediFormGroup.get("clearinghouseProfile").value;
    edi.clearinghouseS3Bucket =
      this.ediFormGroup.get("clearinghouseProfile").value == "None"
        ? ""
        : this.ediFormGroup.get("clearinghouseProfile").value == null
        ? ""
        : this.getS3BucketConfigFromClearinghouseId(
            this.ediFormGroup.get("clearinghouseProfile").value
          );
    edi.clearinghouseTenantNPI =
      this.ediFormGroup.get("clearinghouseProfile").value == "None"
        ? ""
        : this.ediFormGroup.get("clearinghouseProfile").value == null
        ? ""
        : this.getNpiFromClearinghouse(
            this.ediFormGroup.get("clearinghouseProfile").value,
            this.selectedBenefitType
          );
    edi.payerTypeCode = this.ediFormGroup.get("ediPayerTypeCode").value;
    if (this.showSecondaryProviderId) {
      edi.secondaryProviderId =
        this.ediFormGroup.get("ediSecondaryProviderId").value || "";
      // CO-3701: MCP is Curantis-Managed always-required (no admin toggle), so persist
      // `true` rather than the hidden checkbox's value. The server honors an explicit
      // toggle over the MCP default, so a stale/unchecked `false` would wrongly un-require
      // an MCP payer.
      edi.secondaryProviderIdRequired = this.currentPayer.isManagedCare
        ? true
        : !!this.ediFormGroup.get("ediSecondaryProviderIdRequired").value;
    }
    return edi;
  }

  getS3BucketConfigFromClearinghouseId(clearinghouseId: string): string {
    let selectedClearinghouse = this.clearinghouseProfileList.find(
      (cs) => cs.value == clearinghouseId
    );
    return selectedClearinghouse == null
      ? ""
      : selectedClearinghouse.clearinghouseData.clearinghouseS3Bucket
      ? selectedClearinghouse.clearinghouseData.clearinghouseS3Bucket
      : selectedClearinghouse.clearinghouseData.ClearinghouseS3Bucket;
  }

  getNpiFromClearinghouse(
    clearinghouseId: string,
    benefitType: string
  ): string {
    const selectedClearinghouse = this.clearinghouseProfileList.find(
      (cs) => cs.value === clearinghouseId
    );

    const providerIndices =
      selectedClearinghouse?.clearinghouseData?.providerIndices;
    if (!providerIndices) {
      return "";
    }

    let searchKey = benefitType.toUpperCase();
    if (searchKey === "ROOMANDBOARD") {
      searchKey = "HOSPICE";
    }

    const matchingKey = Object.keys(providerIndices).find(
      (key) => key.toUpperCase() === searchKey
    );

    if (matchingKey) {
      const providerIndex = providerIndices[matchingKey];
      return providerIndex?.NPI ?? "";
    }
    return "";
  }

  updateEdiData(edi: Edi, displayName: string) {
    this.benefitTypes.set(edi.benefitType, {
      payerName: edi.payerName,
      receiverId: edi.receiverId,
      submitterId: edi.submitterId,
      clearinghouseProfile:
        edi.clearinghouseId == "" ? "None" : edi.clearinghouseId,
      payerTypeCode: edi.payerTypeCode,
      displayName: displayName,
      secondaryProviderId: edi.secondaryProviderId || "",
      secondaryProviderIdRequired: edi.secondaryProviderIdRequired,
    });
  }

  showAlert = ({ title, message }) => {
    let alert = this.alertController.create({
      title,
      message,
      buttons: [
        {
          text: "Ok",
        },
      ],
    });
    alert.present();
  };

  checkTabEnabled(benefitType: string) {
    return (
      this.currentPayer &&
      this.currentPayer.benefitType &&
      this.currentPayer.benefitType
        .replace(/ /g, "")
        .toUpperCase()
        .indexOf(benefitType) >= 0
    );
  }

  private onSaveError = (error) => {
    this.saveEnabled = true;
    this.utilityService.decreaseSpinnerCount();
    if (error instanceof HttpErrorResponse) {
      if (error.error) {
        this.showAlert({
          title: "Error",
          message: error.error.message,
        });
      } else {
        this.showAlert({
          title: "Error",
          message: "Error while saving EDI info. " + error.message,
        });
      }
    }
  };

  onClearingHouseProfileChange(event: any) {
    this.updateSubmitterIdValidators(event);
  }

  updateSubmitterIdValidators(clearinghouseProfile: any) {
    this.submitterIdRequired =
      clearinghouseProfile == null ||
      clearinghouseProfile === "" ||
      clearinghouseProfile == "None" ||
      (this.currentPayer.payerType.toUpperCase() == "MEDICARE" &&
        (this.selectedBenefitType == "HOSPICE" ||
          this.selectedBenefitType == "PALLIATIVE"));
    if (this.submitterIdRequired) {
      this.ediFormGroup
        .get("ediSubmitterId")
        .setValidators([
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(15),
        ]);
    } else {
      this.ediFormGroup
        .get("ediSubmitterId")
        .setValidators([Validators.minLength(2), Validators.maxLength(15)]);
    }
    this.ediFormGroup.get("ediSubmitterId").updateValueAndValidity();
  }

  getPayerRequest() {
    let value = {
      payerId: this.currentPayer.payerId,
      tenantId: this.currentPayer.tenantId,
      payerName: this.currentPayer.payerName,
      payerType: this.currentPayer.payerType,
      benefitType: this.currentPayer.benefitType,
      plans: this.currentPayer.plans,
      payerAddress: this.currentPayer.payerAddress,
      contactNumber: this.currentPayer.contactNumber,
      useSameAddress: this.currentPayer.useSameAddress,
      payToAddress: this.currentPayer.payToAddress,
      scheduleEligibility: this.currentPayer?.scheduleEligibility || false,
      instantEligibilityClearinghouseId:
        this.currentPayer?.instantEligibilityClearinghouseId ?? null,
    };

    return value;
  }
}



// WEBPACK FOOTER //
// ./src/components/payers/payer-edi/payer-edi.ts